import { Search } from "lucide-react";

import { CityMap } from "@/features/portal/components/city-map";
import { Monogram } from "@/features/portal/components/portal-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** Where the sample people sit on the drawn map. */
const PINS = [
  { id: "kasia", name: () => m.features_sample_coach_full(), spot: "left-[50%] top-[30%]", lit: true },
  { id: "michal", name: () => m.coaches_page_sample_two(), spot: "left-[24%] top-[52%]" },
  { id: "ania", name: () => m.coaches_page_sample_three(), spot: "left-[76%] top-[46%]" },
];

const PEOPLE = [
  {
    id: "kasia",
    name: () => m.features_sample_coach_full(),
    detail: () => m.coaches_page_sample_one_detail(),
    times: ["17:30", "18:30", "19:30"],
    lit: true,
  },
  {
    id: "michal",
    name: () => m.coaches_page_sample_two(),
    detail: () => m.coaches_page_sample_two_detail(),
    times: ["08:00", "12:30"],
  },
];

/**
 * The directory as a night map: people with open times pinned around town, and the rows a visitor
 * taps a time in. Sample people; the real directory is the list further down the page.
 */
export const CoachFinder = ({ className }: { className?: string }) => (
  <div aria-hidden className={cn("absolute inset-0", className)}>
    <CityMap className="absolute inset-0" />
    <span className="absolute inset-0 bg-[radial-gradient(120%_85%_at_50%_0%,transparent_40%,oklch(0.13_0.004_120)_100%)]" />
    {PINS.map((pin) => (
      <span key={pin.id} className={cn("absolute -translate-x-1/2 -translate-y-1/2", pin.spot)}>
        <span className="relative grid place-items-center">
          {pin.lit ? (
            <span className="absolute inset-0 animate-ping rounded-full bg-primary/40 motion-reduce:hidden" />
          ) : null}
          <Monogram
            name={pin.name()}
            className={cn(
              "relative rounded-full ring-4 ring-[oklch(0.13_0.004_120)]",
              pin.lit ? "size-14 text-[17px]" : "size-10 text-[13px] opacity-80",
            )}
          />
        </span>
      </span>
    ))}
    <div className="absolute inset-x-4 bottom-4 rounded-[24px] bg-[oklch(0.2_0.004_120/0.8)] p-2 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.7)] ring-1 ring-white/10 backdrop-blur-xl ring-inset md:inset-x-auto md:left-6 md:w-[380px]">
      <div className="flex h-11 items-center gap-2.5 rounded-[16px] bg-foreground/[0.06] px-3.5 text-[15px] text-muted-foreground">
        <Search className="size-4" />
        {m.coaches_page_search()}
      </div>
      <ul className="mt-1">
        {PEOPLE.map((person) => (
          <li key={person.id} className={cn("rounded-[16px] px-2.5 py-2.5", person.lit && "bg-foreground/[0.06]")}>
            <span className="flex items-center gap-3">
              <Monogram name={person.name()} className="size-11 rounded-[14px] text-[14px]" />
              <span className="min-w-0 flex-1 leading-tight">
                <b className="block truncate text-[15px] font-semibold">{person.name()}</b>
                <small className="block truncate text-[13px] text-muted-foreground">{person.detail()}</small>
              </span>
            </span>
            <span className="mt-2.5 flex gap-1.5 pl-14">
              {person.times.map((time, index) => (
                <span
                  key={time}
                  className={cn(
                    "rounded-full px-3 py-1.5 text-[13px] font-semibold tabular-nums",
                    person.lit && index === 0 ? "bg-primary text-primary-foreground" : "bg-foreground/[0.08]",
                  )}
                >
                  {time}
                </span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);
